import { useEffect, useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import { formatTimestamp } from "../../../../utils";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import SearchableMultiselectDropdown from "../../../../common/components/xepp/searchable-multiselect-dropdown/SearchableMultiselectDropdown";

import styles from "./steps.module.scss";
import Can from "../../../../guards/Can";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { StepInstanceResponse } from "../../../../model/workflows/step-instance";
import {
	useLazyGetAllClaimableStepsQuery,
	useClaimStepMutation,
} from "../../../../service/customersupport-service";
import { useLazyGetUsersForFilterQuery } from "../../../../service/user-service";
import { WorkflowsMenuActiveIcon } from "../../../../assets/sidemenu-icons";
import { DateRangeFilter } from "../../../../common/components/xepp/date-range-filter/DateRangeFilter";
import { FilterIcon2 } from "../../../../assets/icons";

interface ClaimableStepsPageProps {
	departmentId?: string;
	onCountChange?: (count: number) => void;
}

export const ClaimableStepsPage = ({ departmentId, onCountChange }: ClaimableStepsPageProps) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [nameSearchTerm, setNameSearchTerm] = useState<string>("");
	const debouncedNameSearchTerm = useDebounce(nameSearchTerm, 500);
	const [page, setPage] = useState(1);
	const pageSize = 15;
	// Default table filter: 1st of current month to today
	const now = new Date();
	const [fromDate, setFromDate] = useState<Date | null>(new Date(now.getFullYear(), now.getMonth(), 1));
	const [toDate, setToDate] = useState<Date | null>(now);
	const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
	const [claimingStepId, setClaimingStepId] = useState<string | null>(null);
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

	const [getClaimableSteps, { data, isLoading, isFetching }] =
		useLazyGetAllClaimableStepsQuery();
	const [claimStep] = useClaimStepMutation();
	const [getUsersForFilter, { data: usersFilterData, isFetching: isLoadingUsers }] = useLazyGetUsersForFilterQuery();

	const { count } = data ?? {};

	useEffect(() => {
		if (count !== undefined) onCountChange?.(count);
	}, [count, onCountChange]);

	// Fetch filter options on mount and when name search changes
	useEffect(() => {
		const filterParams: any = departmentId ? { departmentId } : {};
		if (debouncedNameSearchTerm) {
			filterParams.search = debouncedNameSearchTerm;
		}
		getUsersForFilter(filterParams);
	}, [departmentId, debouncedNameSearchTerm, getUsersForFilter]);

	useEffect(() => {
		const queryParams = {
			page,
			pageSize,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
			...(departmentId && { departmentId }),
			...(fromDate && { dateFrom: fromDate.toISOString() }),
			...(toDate && { dateTo: toDate.toISOString() }),
			...(selectedUserIds.length > 0 && { customerIds: selectedUserIds }),
		};

		getClaimableSteps(queryParams);
	}, [page, pageSize, debouncedSearchTerm, departmentId, fromDate, toDate, selectedUserIds, getClaimableSteps]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleApplyDateFilter = (from: Date | null, to: Date | null) => {
		setFromDate(from);
		setToDate(to);
	};

	const formatDateRange = () => {
		if (!fromDate || !toDate) return "Add Filter";
		const from = fromDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
		const to = toDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
		return `${from} - ${to}`;
	};
	const hasActiveDateFilter = Boolean(fromDate || toDate);

	// Combine customers and staff for name filter
	const allUsers = [
		...(usersFilterData?.customers ?? []).map(c => ({ id: c.id, label: `Customer: ${c.displayName}` })),
		...(usersFilterData?.staff ?? []).map(s => ({ id: s.id, label: `Staff: ${s.displayName}` })),
	];

	const handleUserSelection = (id: string) => {
		setSelectedUserIds(prev =>
			prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
		);
		setPage(1);
	};

	const clearNameFilter = () => {
		setSelectedUserIds([]);
		setPage(1);
	};

	const handleClaim = async (stepInstance: StepInstanceResponse) => {
		setClaimingStepId(stepInstance.id);
		try {
			await claimStep({ stepInstanceId: stepInstance.id }).unwrap();
			toast.success("Successfully claimed step");
		} catch (error) {
			toast.error("Failed to claim step");
		} finally {
			setClaimingStepId(null);
		}
	};

	const getSlaStatus = (stepInstance: StepInstanceResponse) => {
		if (stepInstance.isOverdue) {
			return { className: styles.overdue, text: "Overdue" };
		}

		const dueDate = new Date(stepInstance.due);
		const now = new Date();
		const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);

		if (hoursUntilDue <= 24) {
			return { className: styles.warning, text: "Due Soon" };
		}

		const daysUntilDue = Math.ceil(hoursUntilDue / 24);
		return { className: styles.normal, text: `${daysUntilDue} Day${daysUntilDue > 1 ? "s" : ""}` };
	};

	const getSlaDisplay = (stepInstance: StepInstanceResponse): string => {
		if (!stepInstance.stepDefinition) return "-";

		const { slaDuration, slaDurationType } = stepInstance.stepDefinition;

		// slaDurationType: 0 = Hours, 1 = Days
		if (slaDurationType === 0) { // Hours
			return `${slaDuration} ${slaDuration === 1 ? "Hour" : "Hours"}`;
		}

		// Days
		return `${slaDuration} ${slaDuration === 1 ? "Day" : "Days"}`;
	};

	const getRequestType = (record: StepInstanceResponse) =>
		record.customerServiceRequestInstance?.serviceRequest?.name
		|| record.additionalServiceRequestInstance?.serviceRequest?.name
		|| "-";

	const getCustomerName = (record: StepInstanceResponse) => {
		if (record.customer) {
			return `${record.customer.firstName} ${record.customer.lastName}`;
		}
		const csr = record.customerServiceRequestInstance;
		if (csr?.customerFirstName || csr?.customerLastName) {
			return `${csr.customerFirstName ?? ""} ${csr.customerLastName ?? ""}`.trim();
		}
		return "-";
	};

	const columns: Array<ColumnType<StepInstanceResponse>> = [
		{
			accessor: "requestType",
			header: "Service Request",
			render: (record: StepInstanceResponse) => (
				<span className={styles.requestType}>
					{getRequestType(record)}
				</span>
			),
		},
		{
			accessor: "customerName",
			header: "Name of Customer",
			render: (record: StepInstanceResponse) => (
				<span className={styles.customerName}>
					{getCustomerName(record)}
				</span>
			),
		},
		{
			accessor: "dateTime",
			header: "Created At",
			render: (record: StepInstanceResponse) => (
				<span className={styles.dateTime}>
					{formatTimestamp(record.createdAt)}
				</span>
			),
		},
		{
			accessor: "sla",
			header: "SLA",
			render: (record: StepInstanceResponse) => (
				<span className={styles.slaValue}>{getSlaDisplay(record)}</span>
			),
		},
		{
			accessor: "status",
			header: "Status",
			render: (record: StepInstanceResponse) => {
				const slaStatus = getSlaStatus(record);
				return (
					<span className={`${styles.slaBadge} ${slaStatus.className}`}>
						{slaStatus.text}
					</span>
				);
			},
		},
		{
			accessor: "id",
			header: "",
			render: (record: StepInstanceResponse) => {
				const isThisStepClaiming = claimingStepId === record.id;
				return (
					<Can permission={UtilityPermissions.ClaimServiceRequests}>
						<Button
							variant="info"
							text={isThisStepClaiming ? "Claiming..." : "Claim"}
							onClick={() => handleClaim(record)}
							disabled={isThisStepClaiming}
							className={styles.actionButton}
						/>
					</Can>
				);
			},
		},
	];

	const headerActions = (
		<div className={styles.tableHeaderActions}>
			<div className={styles.searchContainer}>
				<SearchInput
					initialValue={searchTerm}
					onSearch={(value: string) => setSearchTerm(value)}
					placeholder="Search step name"
				/>
				{isFetching && <ClipLoader size={20} color="#DE841B" />}
			</div>
			<div className={styles.filtersRow}>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>Name</span>
					<span className={styles.filterDivider} />
					<SearchableMultiselectDropdown
						items={allUsers}
						checkedIds={selectedUserIds}
						placeholder="Search by name or email"
						onSearch={(value: string) => setNameSearchTerm(value)}
						onSelectionChange={handleUserSelection}
						debounceTime={300}
						isLoading={isLoadingUsers}
					/>
					{selectedUserIds.length > 0 && (
						<Button text="Clear" variant="text" onClick={clearNameFilter} />
					)}
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>Date</span>
					<span className={styles.filterDivider} />
					<Button text={formatDateRange()} variant="outline" onClick={() => setIsDateFilterOpen(true)} leftIcon={<FilterIcon2 />} />
					{hasActiveDateFilter && <Button text="Clear" variant="text" onClick={() => handleApplyDateFilter(null, null)} />}
				</div>
			</div>
		</div>
	);

	return (
		<div className={styles.historyContainer}>
			<DataTable<StepInstanceResponse>
				data={data?.items ?? []}
				loading={isLoading}
				isUpdating={isFetching}
				columns={columns}
				headerActions={headerActions}
				emptyComponent={
					<EmptyState
						icon={<WorkflowsMenuActiveIcon />}
						title="No claimable steps found"
					/>
				}
			/>
			<TablePagination
				isLoading={isFetching}
				totalItems={count ?? 0}
				itemsPerPage={pageSize}
				currentPage={page}
				onPageChange={setPage}
			/>
			<DateRangeFilter
				isOpen={isDateFilterOpen}
				onClose={() => setIsDateFilterOpen(false)}
				onApply={handleApplyDateFilter}
				initialFrom={fromDate}
				initialTo={toDate}
			/>
		</div>
	);
};
